import { useState } from "react";
import { Html, SiteCopy, siteData } from "../copy";
import { CONTACT_HREF, SEMINAR_HREF } from "../links";

/** "See it working, Tuesdays at 11" (the prototype's webinarBuild). */
export function WebinarStrip() {
  return (
    <div className="webstrip rv">
      <div className="wrap webin">
        <div>
          <SiteCopy as="b" k="webinar.title" />
          <SiteCopy as="p" k="webinar.body" />
        </div>
        <a className="sbtn p" href={SEMINAR_HREF}>
          <SiteCopy k="webinar.cta" />
        </a>
      </div>
    </div>
  );
}

/**
 * The headcount calculator: $5 per employee a month for founding clients, with the regular price
 * beside it so the discount is legible. No urgency, no countdown.
 */
const SEAT = 5;
export function HeadcountCalc({ id }: { id?: string }) {
  const [n, setN] = useState(100);
  const set = (v: string) => {
    let x = parseInt(v, 10);
    if (Number.isNaN(x)) x = 0;
    setN(Math.max(0, Math.min(100000, x)));
  };
  const mo = n * SEAT;
  return (
    <div className="calcwrap rv d1" id={id}>
      <div className="calcbox">
        <div className="calcin">
          <label htmlFor="calc-n">How many people work for you?</label>
          <div className="calcfield">
            <input
              id="calc-n"
              type="number"
              min={1}
              max={100000}
              value={n}
              inputMode="numeric"
              onChange={(e) => set(e.target.value)}
            />
            <span className="suf">people</span>
          </div>
          <input
            className="calcrange"
            type="range"
            min={20}
            max={500}
            step={5}
            value={Math.max(20, Math.min(500, n))}
            onChange={(e) => set(e.target.value)}
            aria-label="Headcount"
          />
          <SiteCopy as="p" className="calchint" k="emp.calc.foot" />
        </div>
        <div className="calcout" aria-live="polite">
          <div className="co-row">
            <span>Every month</span>
            <b data-testid="calc-month">${mo.toLocaleString("en-CA")}</b>
          </div>
          <div className="co-row">
            <span>Every year</span>
            <b>${(mo * 12).toLocaleString("en-CA")}</b>
          </div>
          <SiteCopy as="div" className="co-note" k="emp.calc.note" />
          <a className="sbtn p" href={CONTACT_HREF}>
            <SiteCopy k="cta.talk" />
          </a>
        </div>
      </div>
    </div>
  );
}

/** The three asks (the prototype's asksBuild). */
export function Asks({ calcId }: { calcId: string }) {
  const items = siteData<[string, string, string, string, string][]>("emp.asks.items");
  return (
    <div className="asks rv d1" id="asks">
      {items.map((a, i) => {
        const cls = `sbtn${i === 2 ? " p" : ""}`;
        const label = <Html html={a[3]} />;
        return (
          <div className={`ask a${i}`} key={a[0]}>
            <div className="akh">
              <Html as="b" html={a[0]} />
              <Html html={a[1]} />
            </div>
            <Html as="p" html={a[2]} />
            {a[4] === "calc" ? (
              <button
                type="button"
                className={cls}
                onClick={() =>
                  document
                    .getElementById(calcId)
                    ?.scrollIntoView({ behavior: "smooth", block: "center" })
                }
              >
                {label}
              </button>
            ) : (
              <a className={cls} href={a[4] === "webinar" ? SEMINAR_HREF : CONTACT_HREF}>
                {label}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The employer dashboard mock. Workforce-level only: every figure is a count or a population
 * average, and the page labels the numbers as illustrative.
 */
export function HrDash() {
  const kpi = [
    ["Invited", "420", "everyone eligible"],
    ["Activated", "287", "68% of those invited"],
    ["Plans built", "264", "92% of those activated"],
    ["Active this month", "198", "69% of those activated"],
  ];
  const months = [28, 46, 71, 103, 142, 178, 211, 239, 258, 271, 281, 287];
  const mx = Math.max(...months);
  const acts: [string, number][] = [
    ["Reviewed their debt order", 74],
    ["Moved idle cash", 61],
    ["Set up a regular contribution", 52],
    ["Corrected a plan assumption", 48],
    ["Modelled a decision before making it", 39],
  ];
  const rings: [string, string, number][] = [
    ["Have a full plan to retirement", "92%", 92],
    ["Scored their own priorities", "88%", 88],
    ["Checked in within 30 days", "69%", 69],
  ];
  const ring = (pct: number, col: string) => {
    const R = 30;
    const C = 2 * Math.PI * R;
    return (
      <svg width="82" height="82" viewBox="0 0 82 82" aria-hidden="true">
        <circle cx="41" cy="41" r={R} fill="none" stroke="rgba(11,42,91,.1)" strokeWidth="9" />
        <circle
          cx="41"
          cy="41"
          r={R}
          fill="none"
          stroke={col}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${((C * pct) / 100).toFixed(1)} ${C.toFixed(1)}`}
          transform="rotate(-90 41 41)"
        />
      </svg>
    );
  };
  return (
    <div className="hrwrap rv d1" id="hrdash">
      <div className="hrwin">
        <div className="hrbar">
          <i />
          <i />
          <i />
          <span className="url">wealthyplanet.com/employer/overview</span>
        </div>
        <div className="hrbody">
          <div className="hrhead">
            <div>
              <div className="ht">Financial wellness overview</div>
              <div className="hs">Northline Logistics &middot; 420 eligible employees</div>
            </div>
            <span className="hrq">Q1 2026</span>
          </div>
          <div className="hrkpi">
            {kpi.map((k) => (
              <div className="hk" key={k[0]}>
                <div className="e">{k[0]}</div>
                <div className="v">{k[1]}</div>
                <div className="s">{k[2]}</div>
              </div>
            ))}
          </div>
          <div className="hrsplit">
            <div className="hrcard">
              <div className="hc">Activation over the year</div>
              <div className="hbars">
                {months.map((m, i) => (
                  <span
                    key={i}
                    style={{ height: `${Math.round((m / mx) * 100)}%` }}
                    title={`Month ${i + 1}`}
                  />
                ))}
              </div>
              <div className="hax">
                <span>Jan</span>
                <span>Jun</span>
                <span>Dec</span>
              </div>
            </div>
            <div className="hrcard">
              <div className="hc">Of the employees who activated</div>
              <div className="hrings">
                {rings.map((r, i) => (
                  <div className="hring" key={r[0]}>
                    {ring(r[2], ["#2E6FBF", "#119B7F", "#E8A90B"][i])}
                    <div className="hrv">{r[1]}</div>
                    <div className="hrl">{r[0]}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="hrcard" style={{ marginTop: 12 }}>
            <div className="hc">Most common actions taken, as a share of active employees</div>
            {acts.map((a) => (
              <div className="hrow" key={a[0]}>
                <span className="n">{a[0]}</span>
                <span className="bar">
                  <span style={{ width: `${a[1]}%` }} />
                </span>
                <span className="p">{a[1]}%</span>
              </div>
            ))}
          </div>
          <div className="hrfoot">
            Counts and population averages only. No employee is identified and no balances, accounts
            or individual plans appear anywhere in this view.
          </div>
        </div>
      </div>
      <p className="hrnote">
        Illustrative figures for a 420-person employer. Your own dashboard reports your own
        workforce.
      </p>
    </div>
  );
}

/** The cost stats, each with its named published source. */
export function EmployerStats() {
  const stats = siteData<[string, string, string][]>("employers.cost.stats");
  return (
    <div className="estat rv d1" id="empstats">
      {stats.map((x) => (
        <div className="ecard" key={x[0]}>
          <Html as="div" className="big" html={x[0]} />
          <Html as="div" className="lb" html={x[1]} />
          <Html as="div" className="src" html={x[2]} />
        </div>
      ))}
    </div>
  );
}

export function EmployerProof() {
  const groups = siteData<[string, string, string, string, string][]>("employers.proof.groups");
  return (
    <div className="egrp rv d1" id="empproof">
      {groups.map((g, i) => (
        <div className={`egcard g${i}`} key={g[0]}>
          <div className="gh">
            <Html as="b" html={g[0]} />
            <Html html={g[1]} />
          </div>
          <Html as="div" className="gv" html={g[2]} />
          <Html as="div" className="gs" html={g[3]} />
          <Html as="p" html={g[4]} />
        </div>
      ))}
    </div>
  );
}

export function EmployerRollout() {
  const steps = siteData<[string, string][]>("employers.roll.steps");
  return (
    <div className="eroll rv d1" id="emproll">
      {steps.map((x, i) => (
        <div className="erstep" key={x[0]}>
          <span className="n">{i + 1}</span>
          <div>
            <Html as="b" html={x[0]} />
            <Html as="p" html={x[1]} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmployerCards() {
  const cards = siteData<[string, string, string][]>("employers.cards");
  return (
    <div className="cards three" id="empcards">
      {cards.map((x) => (
        <div className="card" key={x[0]}>
          <Html as="p" className="mono" html={x[0]} />
          <Html as="h3" html={x[1]} />
          <Html as="p" html={x[2]} />
        </div>
      ))}
    </div>
  );
}
